import {useEffect, useState} from "react"
import {styled} from "@mui/material/styles"
import {Typography, Button} from "@mui/material"
import Grid from "@mui/material/Grid"
import {useNavigate, useParams} from "react-router-dom"

import {Page} from "@indebted/components/Page"
import {AmountInput} from "@indebted/components/AmountInput"
import {useAnalytics} from "@indebted/analytics"
import {LoadingPage} from "@indebted/components/Loading"
import {partialPaymentAPI} from "@indebted/api"
import {ErrorPage} from "@indebted/components/Error"

const StyledAmountInput = styled(AmountInput)`
	background: rgb(255, 255, 255);
`

function AmountSelectionPage() {
	const {secureCode} = useParams()
	const [Component, setComponent] = useState(<LoadingPage />)
	useEffect(() => {
		partialPaymentAPI
			.getAmountPage({secureCode})
			.then((result) => {
				setComponent(<AmountSelection options={result} />)
			})
			.catch((error) => {
				setComponent(<ErrorPage message={error.Message} error={error} />)
			})
	}, [secureCode])

	return Component
}

function AmountSelection({options}) {
	const {secureCode} = useParams()
	const {track} = useAnalytics()
	const [amount, setAmount] = useState()
	const [remainingBalance, setRemainingBalance] = useState(options.OutstandingBalance)
	const [minimumAmountError, setMinimumAmountError] = useState(false)
	const navigate = useNavigate()

	useEffect(() => {
		track("PartialPaymentAmountPageViewed", {
			SecureCode: secureCode,
		})
	}, [secureCode, track])

	useEffect(() => {
		const delayInputTimeoutId = setTimeout(() => {
			if (amount) {
				setMinimumAmountError(amount * 100 < options.MinimumAmount)
			}
		}, 750)
		return () => clearTimeout(delayInputTimeoutId)
	}, [amount, options.MinimumAmount])

	const handleAmountInput = ({target: {value}}) => {
		setAmount(value)

		if (value === null || value === undefined) {
			setRemainingBalance(options.OutstandingBalance)
		} else if (value * 100 > options.OutstandingBalance) {
			setRemainingBalance(0)
		} else {
			setRemainingBalance(options.OutstandingBalance - value * 100)
		}
	}

	const buttonDisabled = () => {
		return !amount || amount * 100 < options.MinimumAmount
	}

	return (
		<Page padded>
			<Grid
				container
				spacing={5}
				columns={12}
				justifyContent="center"
				alignItems="center"
				padding={0}
				style={{width: "100%"}}
			>
				<Grid size={{xs: 12}} style={{paddingLeft: 0}}>
					<Typography variant="h6">{options.I18n.Header}</Typography>
				</Grid>
				<Grid size={{xs: 12}} style={{paddingLeft: 0}}>
					<Typography>{options.I18n.Title + options.CustomerBalance}</Typography>
				</Grid>
				<Grid size={{xs: 12}} style={{paddingLeft: 0, paddingRight: 0}}>
					<Grid
						container
						columns={12}
						spacing={5}
						style={{
							backgroundColor: "#f5f5f8",
							borderRadius: "10px",
							margin: "0px",
							width: "100%",
						}}
						justifyContent="center"
						alignItems="center"
						padding={0}
					>
						<Grid size={{xs: 12}}>
							<StyledAmountInput
								label={options.I18n.AmountInputLabel}
								error={minimumAmountError}
								value={amount}
								currency={options.Currency}
								helperText={minimumAmountError && options.I18n.MinimumAmountErrorMessage}
								onChange={handleAmountInput}
								required
								autoFocus
							/>
						</Grid>
						<Grid size={{xs: 12}}>
							<Typography>
								{options.I18n.RemainingBalanceLabel +
									options.Currency +
									" " +
									(remainingBalance / 100).toFixed(2)}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid size={{xs: 12}} style={{paddingLeft: 0, paddingRight: 0}}>
					<Button
						fullWidth
						disabled={buttonDisabled()}
						onClick={() => {
							track("PartialPaymentAmountButtonClicked", {
								Amount: amount,
								SecureCode: secureCode,
							})
							navigate(`./${options.DefaultPaymentMethod}?amount=${amount}`)
						}}
					>
						{options.I18n.AmountButtonLabel}
					</Button>
				</Grid>
			</Grid>
		</Page>
	)
}

export {AmountSelectionPage}
